
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * DataFeedSubscription struct
 */
class DataFeedSubscription {
  /**
   * Initialize struct
   * @param {!string=} symbol
   * @param {!number=} marketDepth
   * @param {!number=} sortOrder
   * @constructor
   */
  constructor (symbol = '', marketDepth = 0, sortOrder = 0) {
    this.Symbol = symbol
    this.MarketDepth = marketDepth
    this.SortOrder = sortOrder
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DataFeedSubscription}
   * @param {!DataFeedSubscription} other Other struct
   * @returns {!DataFeedSubscription} This struct
   */
  copy (other) {
    if (other.Symbol != null) {
      this.Symbol = other.Symbol
    } else {
      this.Symbol = null
    }
    if (other.MarketDepth != null) {
      this.MarketDepth = other.MarketDepth
    } else {
      this.MarketDepth = null
    }
    if (other.SortOrder != null) {
      this.SortOrder = other.SortOrder
    } else {
      this.SortOrder = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DataFeedSubscription}
   * @returns {!DataFeedSubscription} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DataFeedSubscriptionModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DataFeedSubscriptionModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DataFeedSubscription}
   * @param {!DataFeedSubscription} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DataFeedSubscription)) {
      throw new TypeError('Instance of DataFeedSubscription is required!')
    }
    // noinspection RedundantIfStatementJS
    if (this.SortOrder !== other.SortOrder) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DataFeedSubscription}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Symbol: ((this.Symbol != null) ? this.Symbol : null),
      MarketDepth: ((this.MarketDepth != null) ? this.MarketDepth : null),
      SortOrder: ((this.SortOrder != null) ? this.SortOrder : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DataFeedSubscription.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DataFeedSubscription} other Object value
   * @returns {!DataFeedSubscription} Created struct
   */
  static fromObject (other) {
    return new DataFeedSubscription().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DataFeedSubscription}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DataFeedSubscription.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DataFeedSubscription}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 2056
  }
}

export { DataFeedSubscription };

/**
 * Fast Binary Encoding DataFeedSubscription field model
 */
class FieldModelDataFeedSubscription extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Symbol = new fbe.FieldModelString(buffer, 4 + 4)
    this.MarketDepth = new fbe.FieldModelUInt32(buffer, this.Symbol.fbeOffset + this.Symbol.fbeSize)
    this.SortOrder = new fbe.FieldModelUInt32(buffer, this.MarketDepth.fbeOffset + this.MarketDepth.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelDataFeedSubscription}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDataFeedSubscription}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Symbol.fbeSize + this.MarketDepth.fbeSize + this.SortOrder.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDataFeedSubscription}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Symbol.fbeExtra + this.MarketDepth.fbeExtra + this.SortOrder.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDataFeedSubscription}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDataFeedSubscription.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDataFeedSubscription}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 2056
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDataFeedSubscription}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDataFeedSubscription}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Symbol.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Symbol.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Symbol.fbeSize

    if ((fbeCurrentSize + this.MarketDepth.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarketDepth.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarketDepth.fbeSize

    if ((fbeCurrentSize + this.SortOrder.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SortOrder.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SortOrder.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDataFeedSubscription}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDataFeedSubscription}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDataFeedSubscription}
   * @param {!DataFeedSubscription} fbeValue Default value, defaults is new DataFeedSubscription()
   * @returns {!DataFeedSubscription} DataFeedSubscription value
   */
  get (fbeValue = new DataFeedSubscription()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDataFeedSubscription}
   * @param {!DataFeedSubscription} fbeValue DataFeedSubscription value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Symbol.fbeSize) <= fbeStructSize) {
      fbeValue.Symbol = this.Symbol.get()
    } else {
      fbeValue.Symbol = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Symbol.fbeSize

    if ((fbeCurrentSize + this.MarketDepth.fbeSize) <= fbeStructSize) {
      fbeValue.MarketDepth = this.MarketDepth.get()
    } else {
      fbeValue.MarketDepth = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarketDepth.fbeSize

    if ((fbeCurrentSize + this.SortOrder.fbeSize) <= fbeStructSize) {
      fbeValue.SortOrder = this.SortOrder.get()
    } else {
      fbeValue.SortOrder = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SortOrder.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDataFeedSubscription}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDataFeedSubscription}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDataFeedSubscription}
   * @param {!DataFeedSubscription} fbeValue DataFeedSubscription value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDataFeedSubscription}
   * @param {DataFeedSubscription} fbeValue DataFeedSubscription value
   */
  setFields (fbeValue) {
    this.Symbol.set(fbeValue.Symbol)
    this.MarketDepth.set(fbeValue.MarketDepth)
    this.SortOrder.set(fbeValue.SortOrder)
  }
}

export { FieldModelDataFeedSubscription };

/**
 * Fast Binary Encoding DataFeedSubscription model
 */
class DataFeedSubscriptionModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDataFeedSubscription(this.buffer, 4)
  }

  /**
   * Get the DataFeedSubscription model
   * @this {!DataFeedSubscriptionModel}
   * @returns {!FieldModelDataFeedSubscription} model DataFeedSubscription model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DataFeedSubscriptionModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DataFeedSubscriptionModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DataFeedSubscriptionModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DataFeedSubscriptionModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDataFeedSubscription.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DataFeedSubscriptionModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DataFeedSubscriptionModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DataFeedSubscriptionModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DataFeedSubscriptionModel}
   * @param {!DataFeedSubscription} value DataFeedSubscription value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DataFeedSubscriptionModel}
   * @param {!DataFeedSubscription} value DataFeedSubscription value, defaults is new DataFeedSubscription()
   * @return {!object} Deserialized DataFeedSubscription value and its size
   */
  deserialize (value = new DataFeedSubscription()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DataFeedSubscription(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DataFeedSubscription(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DataFeedSubscriptionModel}
   * @param {!number} prev Previous DataFeedSubscription model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DataFeedSubscriptionModel };
